/**
 * Parse and serialize Education section content for structured card editing (Kickresume-style).
 * Supports multiple entries with degree, institution, dates, description.
 */

export interface EducationEntry {
  id: string;
  degree?: string;
  fieldOfStudy?: string;
  institution?: string;
  city?: string;
  country?: string;
  fromDate?: string;
  toDate?: string;
  currentlyHere?: boolean;
  description?: string;
}

const EDUCATION_JSON_PREFIX = '___EDUCATION_JSON___';

function generateId(): string {
  return `edu-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Parse section content: either JSON (from card) or plain text (from LLM/upload).
 */
export function parseEducationContent(content: string): EducationEntry[] {
  if (!content || !content.trim()) return [{ id: generateId() }];
  const trimmed = content.trim();
  if (trimmed.startsWith(EDUCATION_JSON_PREFIX)) {
    try {
      const json = trimmed.slice(EDUCATION_JSON_PREFIX.length).trim();
      const arr = JSON.parse(json) as unknown;
      if (Array.isArray(arr) && arr.length > 0) {
        return arr.map((item: any) => ({
          id: typeof item.id === 'string' ? item.id : generateId(),
          degree: typeof item.degree === 'string' ? item.degree : '',
          fieldOfStudy: typeof item.fieldOfStudy === 'string' ? item.fieldOfStudy : '',
          institution: typeof item.institution === 'string' ? item.institution : '',
          city: typeof item.city === 'string' ? item.city : '',
          country: typeof item.country === 'string' ? item.country : '',
          fromDate: typeof item.fromDate === 'string' ? item.fromDate : '',
          toDate: typeof item.toDate === 'string' ? item.toDate : '',
          currentlyHere: !!item.currentlyHere,
          description: typeof item.description === 'string' ? item.description : '',
        }));
      }
    } catch {
      // fall through to text parse
    }
  }
  // Plain text: treat as one entry with description = content
  const lines = content.split(/\n/).map((l) => l.trim()).filter(Boolean);
  const firstLine = lines[0] || '';
  return [
    {
      id: generateId(),
      degree: firstLine,
      institution: lines[1] || '',
      description: lines.slice(2).join('\n') || '',
    },
  ];
}

/**
 * Serialize entries to content string (JSON so we can round-trip).
 */
export function serializeEducationContent(entries: EducationEntry[]): string {
  const list = entries.map((e) => ({
    id: e.id,
    degree: e.degree ?? '',
    fieldOfStudy: e.fieldOfStudy ?? '',
    institution: e.institution ?? '',
    city: e.city ?? '',
    country: e.country ?? '',
    fromDate: e.fromDate ?? '',
    toDate: e.toDate ?? '',
    currentlyHere: !!e.currentlyHere,
    description: e.description ?? '',
  }));
  return EDUCATION_JSON_PREFIX + JSON.stringify(list);
}

/** Check if content is our JSON format (from EducationCard). */
export function isEducationJsonContent(content: string): boolean {
  return typeof content === 'string' && content.trim().startsWith(EDUCATION_JSON_PREFIX);
}

/**
 * Convert entries to plain text for preview display when section is rendered as text.
 */
export function educationEntriesToDisplayText(entries: EducationEntry[]): string {
  return entries
    .map((e) => {
      const parts: string[] = [];
      if (e.degree) parts.push(e.degree);
      if (e.institution) parts.push(e.institution);
      if (e.fieldOfStudy) parts.push(e.fieldOfStudy);
      const loc = [e.city, e.country].filter(Boolean).join(', ');
      if (loc) parts.push(loc);
      const dates = [e.fromDate, e.toDate].filter(Boolean).join(' – ');
      if (dates) parts.push(dates);
      if (e.currentlyHere) parts.push('(Current)');
      const header = parts.join(' · ');
      const desc = (e.description || '').trim();
      return desc ? `${header}\n${desc}` : header;
    })
    .join('\n\n');
}

/** Get displayable text for preview: use entries if JSON, else raw content. */
export function getEducationDisplayContent(content: string): string {
  if (isEducationJsonContent(content)) {
    return educationEntriesToDisplayText(parseEducationContent(content));
  }
  return content;
}
